<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { onClickOutside } from "@vueuse/core";
import RenewalAccountDetails from "@/components/dashboard/renewals/RenewalAccountDetails.vue";
import RenewalKpiCards from "@/components/dashboard/renewals/RenewalKpiCards.vue";
import RenewalEditModal from "@/components/dashboard/renewals/RenewalEditModal.vue";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useClientsQuery, type NormalizedClient } from "@/composables/useClientsQuery";
import { useRenewalTracking } from "@/composables/useRenewalTracking";
import { getRenewalKpiCounts, type RenewalKpiCounts } from "@/utils/renewalMetrics";
import type { ClientAccountStatus } from "@/services/clientService";
import { useAuthStore } from "@/store/auth";

const router = useRouter();
const queryClient = useQueryClient();
const authStore = useAuthStore();
const canEditRenewalTracking = computed(() => authStore.canEditRenewalTracking);
const { searchQuery, normalizedClients, isLoading, isError, error } = useClientsQuery();
// Match the account selection used by the DMP Accounts page.
const dmpClients = computed(() => normalizedClients.value.filter(
  (client) => client.servicePlanCode === "dmp"
));
const statusFilter = ref("");
const attentionFilter = ref<keyof RenewalKpiCounts | "">("");
const sortBy = ref("account");
const pageSize = ref<5 | 10 | 20 | "all">(10);
const currentPage = ref(1);
const accountListScroll = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);

const dateColumns = [
  { label: "Onboarding date", field: "onboardingDateValue" },
  { label: "Subscription start", field: "subscriptionStartDateValue" },
  { label: "Renewal date", field: "renewalDateValue" },
  { label: "Payment due date", field: "paymentDueDateValue" },
  { label: "Renewal start", field: "renewalStartDateValue" },
  { label: "Renewal end", field: "renewalEndDateValue" },
] as const;
const attentionOrder: (keyof RenewalKpiCounts)[] = [
  "overdueRenewals", "expiredAccounts", "dueThisMonth",
  "renewalsNextMonth", "recentlyRenewed",
];

const renewalKpiCounts = computed(() => getRenewalKpiCounts(dmpClients.value));
// Use the KPI calculator for each account so filters share its existing rules.
const accountRows = computed(() => dmpClients.value.map((client) => ({
  client,
  attention: getRenewalKpiCounts([client]),
})));
const accountStatusOptions: readonly ClientAccountStatus[] = [
  "For Activation",
  "Active",
  "On Hold",
  "Offboarding",
  "Offboarded",
  "Reactivation Onboarding",
];

function compareDates(left: string, right: string) {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  const leftMissing = Number.isNaN(leftTime);
  const rightMissing = Number.isNaN(rightTime);
  if (leftMissing || rightMissing) return Number(leftMissing) - Number(rightMissing);
  return leftTime - rightTime;
}

function attentionPriority(counts: RenewalKpiCounts) {
  const index = attentionOrder.findIndex((key) => counts[key] > 0);
  return index === -1 ? attentionOrder.length : index;
}

const visibleRows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return accountRows.value.filter(({ client, attention }) => {
    const matchesSearch = !query || [client.companyName, client.referenceCode, client.email]
      .join(" ").toLowerCase().includes(query);
    const matchesStatus = !statusFilter.value || client.clientAccountStatus === statusFilter.value;
    return matchesSearch
      && matchesStatus
      && (!attentionFilter.value || attention[attentionFilter.value] > 0);
  }).sort((left, right) => {
    const byName = left.client.companyName.localeCompare(right.client.companyName, undefined, {
      sensitivity: "base",
    });
    if (sortBy.value === "attention") {
      return attentionPriority(left.attention) - attentionPriority(right.attention)
        || compareDates(left.client.paymentDueDateValue, right.client.paymentDueDateValue)
        || byName;
    }
    if (sortBy.value === "payment" || sortBy.value === "renewal") {
      const field = sortBy.value === "payment" ? "paymentDueDateValue" : "renewalDateValue";
      return compareDates(left.client[field], right.client[field]) || byName;
    }
    return byName;
  });
});
const pageCount = computed(() => pageSize.value === "all"
  ? 1 : Math.max(1, Math.ceil(visibleRows.value.length / pageSize.value)));
const paginatedRows = computed(() => {
  if (pageSize.value === "all") return visibleRows.value;
  const start = (currentPage.value - 1) * pageSize.value;
  return visibleRows.value.slice(start, start + pageSize.value);
});
const firstVisibleRow = computed(() => !visibleRows.value.length ? 0
  : pageSize.value === "all" ? 1 : (currentPage.value - 1) * pageSize.value + 1);
const lastVisibleRow = computed(() => pageSize.value === "all" ? visibleRows.value.length
  : Math.min(currentPage.value * pageSize.value, visibleRows.value.length));
const isExporting = ref(false);
const exportError = ref("");
const exportDisabled = computed(() => isLoading.value || isError.value
  || isExporting.value || !paginatedRows.value.length);
const isExportMenuOpen = ref(false);
const exportMenu = ref<HTMLElement | null>(null);
const exportTrigger = ref<HTMLButtonElement | null>(null);
let exportMenuPinned = false;

function closeExportMenu(restoreFocus = false) {
  isExportMenuOpen.value = false;
  exportMenuPinned = false;
  if (restoreFocus) exportTrigger.value?.focus();
}

function openExportMenu() {
  if (!exportDisabled.value) isExportMenuOpen.value = true;
}

function toggleExportMenu() {
  if (exportDisabled.value) return;
  if (isExportMenuOpen.value && exportMenuPinned) closeExportMenu();
  else {
    isExportMenuOpen.value = true;
    exportMenuPinned = true;
  }
}

function leaveExportMenu() {
  if (!exportMenuPinned && !exportMenu.value?.contains(document.activeElement)) closeExportMenu();
}

function onExportFocusOut(event: FocusEvent) {
  if (!(event.relatedTarget instanceof Node) || !exportMenu.value?.contains(event.relatedTarget)) {
    closeExportMenu();
  }
}

async function focusExportChoice() {
  if (exportDisabled.value) return;
  isExportMenuOpen.value = true;
  exportMenuPinned = true;
  await nextTick();
  exportMenu.value?.querySelector<HTMLButtonElement>("[data-export-format]")?.focus();
}

onClickOutside(exportMenu, () => closeExportMenu());
watch(exportDisabled, (disabled) => { if (disabled) closeExportMenu(); });

watch([searchQuery, statusFilter, attentionFilter, sortBy, pageSize], () => {
  currentPage.value = 1;
});
watch(pageCount, (count) => {
  if (currentPage.value > count) currentPage.value = count;
});
watch([currentPage, searchQuery, statusFilter, attentionFilter, sortBy, pageSize], () => {
  if (accountListScroll.value) accountListScroll.value.scrollTop = 0;
});

function applyKpiFilter(filter: keyof RenewalKpiCounts) {
  searchQuery.value = "";
  statusFilter.value = "";
  attentionFilter.value = filter;
  currentPage.value = 1;
}

function formatDate(value: string) {
  if (!value) return "—";
  const parsed = /^\d{4}-\d{2}-\d{2}/.test(value)
    ? new Date(value.slice(0, 10) + "T00:00:00") : new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function displayEmail(client: NormalizedClient) {
  return client.email && client.email !== "No email provided" ? client.email : "—";
}

function exportDate(value: string) {
  const iso = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [year, month, day] = iso.split("-").map(Number);
  // Use calendar components for day arithmetic, never local-time date parsing.
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day) return null;
  const days = (timestamp - Date.UTC(1899, 11, 31)) / 86400000;
  // Excel's 1900 calendar includes a fictional February 29.
  return { iso, serial: days + (timestamp >= Date.UTC(1900, 2, 1) ? 1 : 0) };
}

function downloadExport(blob: Blob, filename: string) {
  if (unmounted) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    // Let the browser start the download before releasing the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

async function exportVisibleAccounts(format: "csv" | "xlsx") {
  if (exportDisabled.value || unmounted) return;
  closeExportMenu(true);
  // Snapshot the displayed page before loading the export library. Later table
  // changes must not change the rows selected by this click.
  const headers = ["Account", "Email", ...dateColumns.map((column) => column.label), "Account status"];
  const rows = paginatedRows.value.map(({ client }) => [
    client.companyName || "—",
    displayEmail(client),
    ...dateColumns.map((column) => exportDate(client[column.field])),
    client.clientAccountStatus,
  ]);
  const today = new Date();
  const date = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0")].join("-");
  const filename = "renewal-dmp-accounts-" + date + "-rows-"
    + firstVisibleRow.value + "-" + lastVisibleRow.value + "." + format;
  isExporting.value = true;
  exportError.value = "";
  try {
    if (format === "csv") {
      const csvRows = [headers, ...rows].map((row) => row.map((value) => {
        let text = typeof value === "string" ? value : value?.iso || "—";
        if (/^\s*[=+\-@]/.test(text)) text = "'" + text;
        return '"' + text.replace(/"/g, '""') + '"';
      }).join(","));
      downloadExport(new Blob(["\uFEFF" + csvRows.join("\r\n")], {
        type: "text/csv;charset=utf-8;",
      }), filename);
      return;
    }
    const XLSX = await import("xlsx");
    if (unmounted) return;
    const data = rows.map((row) => row.map((value) => typeof value === "string" ? value
      : value ? { t: "n", v: value.serial, z: "mmm d, yyyy" } : "—"));
    const sheet = XLSX.utils.aoa_to_sheet([["DMP accounts " + date], headers, ...data]);
    sheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    sheet["!autofilter"] = { ref: XLSX.utils.encode_range({
      s: { r: 1, c: 0 }, e: { r: rows.length + 1, c: headers.length - 1 },
    }) };
    sheet["!cols"] = [{ wch: 32 }, { wch: 36 }, ...dateColumns.map(() => ({ wch: 20 })), { wch: 26 }];
    sheet["!rows"] = [{ hpt: 30 }, { hpt: 28 }, ...rows.map(() => ({ hpt: 22 }))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Renewal");
    // CE ignores custom cell fonts. Use its bundled ZIP support to add the two
    // font styles directly to this generated workbook, preserving date styles.
    const zip = XLSX.CFB.read(new Uint8Array(XLSX.write(workbook, { bookType: "xlsx", type: "array" })), { type: "array" });
    const namespace = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
    const readXml = (path: string): XMLDocument => {
      // CFB paths containing a slash must start at the archive root.
      const entry = XLSX.CFB.find(zip, "/" + path);
      if (!entry) throw new Error("Missing workbook XML: " + path);
      const xml = new DOMParser().parseFromString(new TextDecoder().decode(entry.content), "application/xml");
      if (xml.getElementsByTagName("parsererror").length) throw new Error("Invalid workbook XML");
      return xml;
    };
    const saveXml = (path: string, xml: XMLDocument) => XLSX.CFB.utils.cfb_add(
      zip, "/" + path, new TextEncoder().encode(new XMLSerializer().serializeToString(xml))
    );
    const styles = readXml("xl/styles.xml");
    const fonts = styles.getElementsByTagNameNS(namespace, "fonts")[0];
    const cellStyles = styles.getElementsByTagNameNS(namespace, "cellXfs")[0];
    const baseFont = fonts?.firstElementChild;
    const baseStyle = cellStyles?.firstElementChild;
    if (!baseFont || !baseStyle) throw new Error("Missing workbook styles");
    const addBoldStyle = (size?: number) => {
      const font = baseFont.cloneNode(true) as Element;
      font.appendChild(styles.createElementNS(namespace, "b"));
      if (size) font.getElementsByTagNameNS(namespace, "sz")[0].setAttribute("val", String(size));
      const style = baseStyle.cloneNode(true) as Element;
      style.setAttribute("fontId", String(fonts.children.length));
      style.setAttribute("applyFont", "1");
      const index = cellStyles.children.length;
      fonts.appendChild(font);
      fonts.setAttribute("count", String(fonts.children.length));
      cellStyles.appendChild(style);
      cellStyles.setAttribute("count", String(cellStyles.children.length));
      return String(index);
    };
    const titleStyle = addBoldStyle(16);
    const headerStyle = addBoldStyle();
    const worksheet = readXml("xl/worksheets/sheet1.xml");
    Array.from(worksheet.getElementsByTagNameNS(namespace, "c")).forEach((cell) => {
      const reference = cell.getAttribute("r") || "";
      if (reference === "A1") cell.setAttribute("s", titleStyle);
      else if (/^[A-Z]+2$/.test(reference)) cell.setAttribute("s", headerStyle);
    });
    saveXml("xl/styles.xml", styles);
    saveXml("xl/worksheets/sheet1.xml", worksheet);
    const bytes = XLSX.CFB.write(zip, { fileType: "zip", type: "array", compression: true });
    downloadExport(new Blob([new Uint8Array(bytes)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), filename);
  } catch {
    if (!unmounted) exportError.value = "Unable to export these accounts. Please try again.";
  } finally {
    if (!unmounted) isExporting.value = false;
  }
}

function statusClass(status: string) {
  if (status === "Offboarded") return "border-slate-200 bg-slate-100 text-slate-700";
  if (status === "Offboarding") return "border-rose-200 bg-rose-50 text-rose-700";
  if (status === "On Hold") return "border-amber-200 bg-amber-50 text-amber-800";
  if (status === "Grace Period") return "border-orange-200 bg-orange-50 text-orange-800";
  if (status === "For Activation") return "border-sky-200 bg-sky-50 text-sky-800";
  if (status === "Reactivation Onboarding") return "border-purple-200 bg-purple-50 text-purple-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-800";
}

// Keep selection independent of table filters and fetch details only for a row
// action. The shared tracking composable and its other callers stay unchanged.
const activeClientId = ref<NormalizedClient["id"] | null>(null);
const activeClients = computed(() => dmpClients.value.filter(
  (client) => activeClientId.value !== null && String(client.id) === String(activeClientId.value)
));
const {
  selectedClient, selectedClientDetail, detailIsLoading, detailIsError, refetchDetails,
} = useRenewalTracking(activeClients);
const selectedAccountStatus = computed(() => {
  if (!selectedClient.value) return undefined;
  const label = selectedClientDetail.value?.client_account_status
    ?? selectedClient.value.clientAccountStatus;
  return { label, badgeClass: statusClass(label) };
});
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const editingClient = ref<NormalizedClient | null>(null);
const editingNote = ref<string | null>(null);
const editingAccountStatus = ref("");
const preparingEdit = ref(false);
const editLoadError = ref(false);
const retryingAccounts = ref(false);
let editRequest = 0;
let unmounted = false;
let returnFocusTo: HTMLElement | null = null;
let focusTimer: ReturnType<typeof setTimeout> | undefined;

function rememberFocus(event?: Event) {
  if (focusTimer !== undefined) clearTimeout(focusTimer);
  if (event?.currentTarget instanceof HTMLElement) returnFocusTo = event.currentTarget;
}

async function restoreRowFocus() {
  await nextTick();
  if (unmounted) return;
  if (focusTimer !== undefined) clearTimeout(focusTimer);
  // Run after the existing edit dialog's own focus-scope cleanup.
  focusTimer = setTimeout(() => {
    if (showDetailsModal.value || showEditModal.value || preparingEdit.value) return;
    const target = returnFocusTo?.isConnected ? returnFocusTo : searchInput.value;
    target?.focus({ preventScroll: true });
  }, 0);
}

function closeDetailsFocus(event: Event) {
  event.preventDefault();
  void restoreRowFocus();
}

function openDetails(client: NormalizedClient, event: Event) {
  if (preparingEdit.value) return;
  rememberFocus(event);
  editLoadError.value = false;
  activeClientId.value = client.id;
  showDetailsModal.value = true;
}

async function openEditModal(client: NormalizedClient | null, event?: Event) {
  if (!canEditRenewalTracking.value || !client || preparingEdit.value) return;
  rememberFocus(event);
  const request = ++editRequest;
  activeClientId.value = client.id;
  preparingEdit.value = true;
  editLoadError.value = false;
  showDetailsModal.value = false;
  try {
    await nextTick();
    if (request !== editRequest) return;
    const result = await refetchDetails();
    if (request !== editRequest || !canEditRenewalTracking.value) return;
    if (result.isError || !result.data) throw new Error("Account details unavailable");
    editingClient.value = activeClients.value[0] ?? null;
    if (!editingClient.value) throw new Error("Account no longer available");
    // Snapshot loaded fields so background refreshes cannot overwrite typing.
    editingNote.value = result.data.renewal_note ?? null;
    editingAccountStatus.value = result.data.client_account_status
      ?? editingClient.value.raw?.client_account_status
      ?? editingClient.value.clientAccountStatus;
    showEditModal.value = true;
  } catch {
    if (request === editRequest) editLoadError.value = true;
  } finally {
    if (request === editRequest) {
      preparingEdit.value = false;
      if (!showEditModal.value) void restoreRowFocus();
    }
  }
}

watch(showEditModal, (isOpen, wasOpen) => {
  if (!isOpen && wasOpen) {
    editingClient.value = null;
    void refetchDetails();
    void restoreRowFocus();
  }
});
watch(canEditRenewalTracking, (canEdit) => {
  if (!canEdit) {
    ++editRequest;
    preparingEdit.value = false;
    showEditModal.value = false;
    editLoadError.value = false;
    void restoreRowFocus();
  }
});
onBeforeUnmount(() => {
  unmounted = true;
  ++editRequest;
  if (focusTimer !== undefined) clearTimeout(focusTimer);
});

function openFullProfile() {
  const routeKey = selectedClient.value?.raw?.route_key;
  if (!routeKey) return;
  showDetailsModal.value = false;
  void router.push({ name: "ClientProfile", params: { id: routeKey } });
}

async function retryAccounts() {
  retryingAccounts.value = true;
  try {
    await queryClient.refetchQueries({ queryKey: ["clients"], exact: true });
  } finally {
    retryingAccounts.value = false;
  }
}
</script>

<template>
  <section class="flex min-h-0 min-w-0 max-w-full flex-col gap-4 p-4 md:p-6">
    <header>
      <p class="text-xs font-bold uppercase tracking-[0.12em] text-indigo-500">
        Account Profiles
      </p>
      <h1 class="mt-1 text-2xl font-bold tracking-tight text-slate-800">
        DMP Accounts
      </h1>
      <p class="mt-1 max-w-2xl text-sm text-slate-500">
        Review every DMP account's renewal timeline and open the details that need attention.
      </p>
    </header>

    <RenewalKpiCards
      :counts="renewalKpiCounts"
      interactive
      :active-filter="attentionFilter"
      @select-filter="applyKpiFilter"
    />

    <div
      v-if="isError"
      role="alert"
      class="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
    >
      <div class="mr-auto">
        <p class="font-semibold">
          Unable to load renewal accounts.
        </p>
        <p>{{ error?.message || "Please try again later." }}</p>
      </div>
      <button
        type="button"
        class="renewal-button"
        :disabled="retryingAccounts"
        @click="retryAccounts"
      >
        {{ retryingAccounts ? "Retrying…" : "Retry" }}
      </button>
    </div>

    <div
      v-if="preparingEdit"
      role="status"
      class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
    >
      Loading renewal details for {{ selectedClient?.companyName || "the selected account" }}…
    </div>
    <div
      v-if="editLoadError"
      role="alert"
      class="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <span class="mr-auto">Unable to load this account's renewal details. Please retry before editing.</span>
      <button
        type="button"
        class="renewal-button"
        @click="openEditModal(selectedClient)"
      >
        Retry edit
      </button>
    </div>

    <section
      class="min-w-0 overflow-hidden rounded-2xl border border-[#e2e8f1] bg-white shadow-sm"
      aria-label="Renewal accounts"
    >
      <div class="flex flex-wrap items-center gap-3 p-5 pb-4">
        <div class="mr-auto">
          <h2 class="text-lg font-bold text-[#173352]">
            DMP accounts
          </h2>
          <p class="text-xs text-[#758299]">
            {{ visibleRows.length }} matching accounts · {{ dmpClients.length }} total DMP accounts
          </p>
        </div>
        <label class="flex items-center gap-2 text-xs font-semibold text-[#65758d]">
          Show
          <select
            v-model="pageSize"
            class="renewal-control h-9"
            aria-label="Accounts per page"
          >
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option value="all">All</option>
          </select>
          accounts
        </label>
        <div
          ref="exportMenu"
          class="relative w-36 shrink-0"
          :aria-busy="isExporting"
          @mouseenter="openExportMenu"
          @mouseleave="leaveExportMenu"
          @focusout="onExportFocusOut"
          @keydown.esc.prevent="closeExportMenu(true)"
        >
          <button
            ref="exportTrigger"
            type="button"
            class="renewal-button w-full"
            :disabled="exportDisabled"
            :aria-expanded="isExportMenuOpen"
            aria-controls="renewal-export-options"
            title="Export only the rows currently shown on this page"
            @click="toggleExportMenu"
            @keydown.down.prevent="focusExportChoice"
          >
            Export
            <span
              aria-hidden="true"
              class="ml-2 inline-block border-x-[4px] border-t-[5px] border-x-transparent border-t-current"
            />
          </button>
          <div
            v-if="isExportMenuOpen"
            id="renewal-export-options"
            class="absolute inset-x-0 top-full z-40 pt-1"
            role="group"
            aria-label="Export formats"
          >
            <div class="rounded-lg border border-[#dce4ee] bg-white p-1 shadow-lg">
              <button
                type="button"
                data-export-format="csv"
                class="renewal-button block w-full border-0 text-left"
                :disabled="exportDisabled"
                @click="exportVisibleAccounts('csv')"
              >
                CSV
              </button>
              <button
                type="button"
                data-export-format="xlsx"
                class="renewal-button mt-1 block w-full border-0 text-left"
                :disabled="exportDisabled"
                @click="exportVisibleAccounts('xlsx')"
              >
                Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="exportError"
        role="alert"
        class="mx-5 mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      >
        {{ exportError }}
      </p>

      <div class="grid gap-2 border-b border-[#e8edf4] bg-[#fbfcff] p-4 lg:grid-cols-[minmax(220px,1fr)_180px_200px_190px]">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          class="renewal-control h-11"
          placeholder="Search account, email or ID"
          aria-label="Search renewal accounts"
        >
        <select
          v-model="statusFilter"
          class="renewal-control h-11"
          aria-label="Account status"
        >
          <option value="">
            All statuses
          </option>
          <option
            v-for="status in accountStatusOptions"
            :key="status"
            :value="status"
          >
            {{ status }}
          </option>
        </select>
        <select
          v-model="attentionFilter"
          class="renewal-control h-11"
          aria-label="Renewal attention"
        >
          <option value="">
            All accounts
          </option>
          <option value="overdueRenewals">
            Overdue renewals
          </option>
          <option value="dueThisMonth">
            Due this month
          </option>
          <option value="renewalsNextMonth">
            Renewals next month
          </option>
          <option value="recentlyRenewed">
            Recently renewed
          </option>
          <option value="expiredAccounts">
            Expired accounts
          </option>
        </select>
        <select
          v-model="sortBy"
          class="renewal-control h-11"
          aria-label="Sort renewal accounts"
        >
          <option value="account">
            Sort: account name
          </option>
          <option value="attention">
            Sort: attention first
          </option>
          <option value="payment">
            Sort: payment due date
          </option>
          <option value="renewal">
            Sort: renewal date
          </option>
        </select>
      </div>

      <div
        ref="accountListScroll"
        class="max-h-[770px] overflow-auto overscroll-contain focus-visible:outline-indigo-500"
        role="region"
        aria-label="Renewal accounts table"
        :aria-busy="isLoading"
        tabindex="0"
      >
        <table class="renewal-table w-full border-separate border-spacing-0 text-left text-xs">
          <caption class="sr-only">
            Account renewal dates, status and actions
          </caption>
          <thead class="sticky top-0 z-30 bg-[#f8fafd] text-[11px] uppercase tracking-wide text-[#657791]">
            <tr>
              <th
                scope="col"
                class="account-column"
              >
                Account
              </th>
              <th
                v-for="column in dateColumns"
                :key="column.field"
                scope="col"
              >
                {{ column.label }}
              </th>
              <th scope="col">
                Account status
              </th>
              <th
                scope="col"
                class="actions-column"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td
                colspan="9"
                class="h-32 text-center text-[#758299]"
                role="status"
              >
                Loading renewal accounts…
              </td>
            </tr>
            <tr v-else-if="!visibleRows.length">
              <td
                colspan="9"
                class="h-32 text-center text-[#758299]"
              >
                {{ isError ? "Account data is unavailable. Use Retry above." : "No accounts match these filters." }}
              </td>
            </tr>
            <tr
              v-for="{ client } in isLoading ? [] : paginatedRows"
              :key="client.id"
              :data-account-id="client.id"
            >
              <td class="account-column">
                <p class="font-bold text-[#173352]">
                  {{ client.companyName || "—" }}
                </p>
                <p class="mt-1 break-all text-[11px] text-[#758299]">
                  {{ displayEmail(client) }}
                </p>
              </td>
              <td
                v-for="column in dateColumns"
                :key="column.field"
                :data-field="column.field"
                class="text-[#425a76]"
              >
                {{ formatDate(client[column.field]) }}
              </td>
              <td data-field="clientAccountStatus">
                <span
                  class="inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                  :class="statusClass(client.clientAccountStatus)"
                >{{ client.clientAccountStatus }}</span>
              </td>
              <td class="actions-column">
                <div class="flex flex-col items-start gap-2">
                  <button
                    type="button"
                    class="renewal-button"
                    :disabled="preparingEdit"
                    :aria-label="'View details for ' + client.companyName"
                    @click="openDetails(client, $event)"
                  >
                    View details
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-[#e8edf4] px-5 py-4 text-xs text-[#65758d]">
        <span aria-live="polite">Showing {{ firstVisibleRow }}–{{ lastVisibleRow }} of {{ visibleRows.length }} accounts</span>
        <div
          v-if="pageSize !== 'all'"
          class="flex items-center gap-3"
        >
          <button
            type="button"
            class="renewal-button"
            :disabled="currentPage === 1 || isLoading"
            @click="currentPage--"
          >
            Previous
          </button>
          <span>Page {{ currentPage }} of {{ pageCount }}</span>
          <button
            type="button"
            class="renewal-button"
            :disabled="currentPage === pageCount || isLoading"
            @click="currentPage++"
          >
            Next
          </button>
        </div>
      </footer>
    </section>

    <Dialog
      v-if="showDetailsModal"
      v-model:open="showDetailsModal"
    >
      <DialogContent
        class="max-h-[90vh] w-[calc(100%-2rem)] max-w-6xl overflow-y-auto rounded-[1.5rem] border-0 bg-white p-0 pt-6"
        :aria-describedby="undefined"
        @close-auto-focus="closeDetailsFocus"
      >
        <DialogTitle class="sr-only">
          Renewal details for {{ selectedClient?.companyName }}
        </DialogTitle>
        <RenewalAccountDetails
          :client="selectedClient"
          :detail-client="selectedClientDetail"
          :account-status="selectedAccountStatus"
          :is-loading="detailIsLoading"
          :is-error="detailIsError"
          :can-edit="canEditRenewalTracking"
          section-label="Renewal"
          @open-profile="openFullProfile"
          @edit="openEditModal(selectedClient)"
          @retry="refetchDetails()"
        />
      </DialogContent>
    </Dialog>

    <RenewalEditModal
      v-if="editingClient && canEditRenewalTracking"
      v-model:open="showEditModal"
      :client="editingClient"
      :renewal-note="editingNote"
      :account-status="editingAccountStatus"
      :account-status-options="accountStatusOptions"
    />
  </section>
</template>

<style scoped>
.renewal-table { table-layout: fixed; min-width: 1350px; }
.renewal-table th,
.renewal-table td { padding: 0.75rem; vertical-align: top; overflow-wrap: anywhere; }
.renewal-table tbody td { border-top: 1px solid #edf0f5; }
.renewal-table .account-column { position: sticky; left: 0; z-index: 2; width: 280px; background: white; }
.renewal-table thead th { background: #f8fafd; }
.renewal-table thead .account-column { z-index: 32; background: #f8fafd; }
.renewal-table .actions-column { width: 160px; }
.renewal-control { min-width: 0; border: 1.5px solid #dce4ee; border-radius: 0.5rem; background: white; padding: 0 0.75rem; font-size: 0.875rem; color: #173352; }
.renewal-button { border: 1px solid #dce4ee; border-radius: 0.5rem; background: white; padding: 0.4rem 0.65rem; font-size: 0.75rem; font-weight: 600; }
.renewal-button:hover:not(:disabled) { background: #f4f7fc; }
.renewal-button:disabled { cursor: not-allowed; opacity: 0.5; }
.renewal-control:focus-visible,
.renewal-button:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }
</style>
